import { lazy, Suspense } from 'react';
import type { ReactNode } from 'react';
import type { RouteObject } from 'react-router-dom';
import RequirePermission from '@/auth/RequirePermission';
import { Perm } from '@/auth/permissions';
import RequireModule from '@/core/modules/RequireModule';
import { Modules } from '@/core/modules/moduleKeys';
import { parkingSections } from './sections';
import type { ParkingSectionDef } from './context';

// Pages and tabs load when first opened (NFR-04).
const ParkingHomePage = lazy(() => import('./pages/ParkingHomePage'));
const ParkingSectionPage = lazy(() => import('./pages/ParkingSectionPage'));
const FacilityPage = lazy(() => import('./pages/FacilityPage'));
const TariffVersionPage = lazy(() => import('./pages/TariffVersionPage'));
const SessionPage = lazy(() => import('./pages/SessionPage'));
const ReceiptVerifyPage = lazy(() => import('./pages/ReceiptVerifyPage'));

/**
 * The parking module's routes, spread into the signed-in layout. Every page sits behind `RequireModule`,
 * so an organisation without the module sees the "not enabled" card rather than pages meeting 403s.
 */
const guard = (element: ReactNode) => (
  <RequirePermission permission={Perm.Parking.View}>
    <RequireModule module={Modules.Parking}>{element}</RequireModule>
  </RequirePermission>
);

const section = <K extends string>(def: ParkingSectionDef<K>, path: string): RouteObject =>
  ({ path, element: guard(<ParkingSectionPage section={def as unknown as ParkingSectionDef} />) });

export const parkingRoutes: RouteObject[] = [
  { path: 'parking', element: guard(<ParkingHomePage />) },
  section(parkingSections.operations, 'parking/operations/:view?'),
  section(parkingSections.cashier, 'parking/cashier/:view?'),
  section(parkingSections.commercial, 'parking/commercial/:view?'),
  section(parkingSections.devices, 'parking/devices/:view?'),
  section(parkingSections.finance, 'parking/finance/:view?'),
  { path: 'parking/session/:id', element: guard(<SessionPage />) },
  section(parkingSections.vehicles, 'parking/vehicles/:view?'),
  section(parkingSections.settings, 'parking/settings/:view?'),
  { path: 'parking/facility/:id', element: guard(<FacilityPage />) },
  { path: 'parking/tariff-version/:id', element: guard(<TariffVersionPage />) },
];

/** The public check behind a receipt's QR code (no sign-in), outside the signed-in layout. */
export const parkingPublicRoutes: RouteObject[] = [
  { path: '/verify/parking/receipt/:token', element: <Suspense fallback={null}><ReceiptVerifyPage /></Suspense> },
];
